import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Calendar from '../../components/Calendar/Calendar';
import {
    makeSelectShedulesForRange,
    selectSchedulesError,
    selectSchedulesIsSaving,
} from '../../store/chatSchedule/selectors';
import { Month } from './types';
import { clearScheduleError, requestGetSchedules } from '../../store/chatSchedule/actions';
import { getRangeForMonth } from '../../store/chatSchedule/utils';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../../components/Sidebar/Sidebar';
import { Box, Typography } from '@mui/material';
import ScheduleList from './pageComponents/ScheduleList';
import SnackbarMessage from '../../components/SnackbarMessage/SnackbarMessage';
import globalStrings from '../../resources/strings';

const strings = globalStrings.chatSchedulePage;

const today = new Date();
const startSelect = new Date();
startSelect.setMonth(today.getMonth() - 3);
const endSelect = new Date();
endSelect.setMonth(today.getMonth() + 3);

const calendarRange = { startTime: startSelect, endTime: endSelect };

function dateToMonth(date: Date) {
    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
    } as Month;
}

const ChatSchedulePage: React.FC = () => {
    const [month, setMonth] = useState(dateToMonth(today) as Month);
    const [success, setSuccess] = useState('');
    const [selectedDate, setSelectedDate] = useState(null as Date | null);
    const range = useMemo(() => getRangeForMonth(month.year, month.month), [month]);
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(requestGetSchedules(range));
    }, [range]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const setDate = useCallback((d: Date | null, _kbd?: string | undefined) => setSelectedDate(d), []);
    const selectSchedules = useMemo(() => makeSelectShedulesForRange(range), [range]);
    const schedules = useSelector(selectSchedules);

    const error = useSelector(selectSchedulesError);
    const isSaving = useSelector(selectSchedulesIsSaving);

    const wasSaveSet = useRef(false);
    useEffect(() => {
        if (error == null && !isSaving && wasSaveSet.current) {
            //save successful
            setSuccess(strings.scheduleList.success);
        }
        if (isSaving) wasSaveSet.current = true;
    }, [isSaving]);

    const scheduleDays = useMemo(() => schedules.map((s) => s.startTime.getDate()), [schedules]);

    return (
        <div className="content">
            <Sidebar></Sidebar>
            <Box className="main">
                <Box sx={{ marginTop: '2rem', pt: 2.5, pl: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'nowrap' }}>
                        <Calendar
                            availableDates={calendarRange}
                            value={selectedDate}
                            onChange={setDate}
                            onMonthChange={(d) => {
                                setMonth(dateToMonth(d));
                            }}
                            emphasizedDates={scheduleDays}
                            month={month.month}
                        />
                        <Box sx={{ flexShrink: 0, flexGrow: 1 }}>
                            {selectedDate == null && (
                                <Typography sx={{ fontSize: 16, fontWeight: 'bold', mb: 2 }} component="h3">
                                    {strings.selectDate}
                                </Typography>
                            )}
                            {selectedDate != null && <ScheduleList day={selectedDate} />}
                        </Box>
                    </Box>
                </Box>
                {error != null && (
                    <SnackbarMessage
                        serverity="error"
                        message={error ?? ''}
                        clearMessage={() => dispatch(clearScheduleError())}
                    />
                )}
                {success && (
                    <SnackbarMessage
                        serverity="success"
                        message={success ?? ''}
                        clearMessage={() => setSuccess(success)}
                    />
                )}
            </Box>
        </div>
    );
};

export default ChatSchedulePage;
